import { useState, useCallback, useEffect } from 'react';
import api from '../../../../../config/axiosConfig';
import { API } from '../../../../../utils/apiRoutes';

// Helper function to get current date in YYYY-MM-DD format
function getCurrentDateString() {
    const today = new Date();
    return today.toISOString().split('T')[0];
}

export function useOperationForm(selectedEnrollment, onSuccess, onDataRefresh) {
    const [opForm, setOpForm] = useState({
        status: '',
        date_belonged: getCurrentDateString(),
        comment: '',
        payment_method: 1,
        payment_number: '',
        session_count: ''
    });
    
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState({ type: '', text: '' });
    // Renewal fee guard: set when a REGISTER/CUSTOM_REGISTER is attempted on an
    // enrollment whose frozen fee differs from the live course fee.
    const [staleFee, setStaleFee] = useState(null);

    // Reset form when enrollment changes
    useEffect(() => {
        if (selectedEnrollment) {
            setOpForm({
                status: '',
                date_belonged: getCurrentDateString(),
                comment: '',
                payment_method: 1,
                payment_number: '',
                session_count: selectedEnrollment?.course?.registration_sessions ?? ''
            });
            setError('');
            setStaleFee(null);
        }
    }, [selectedEnrollment]);

    // Auto-dismiss success messages after 5 seconds
    useEffect(() => {
        if (message.text && message.type === 'success') {
            const timer = setTimeout(() => {
                setMessage({ type: '', text: '' });
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [message]);

    const handleChange = useCallback((field, value) => {
        let nextForm = { ...opForm, [field]: value };
        if (field === 'status' && ![1, 9, 10].includes(Number(value))) {
            nextForm.payment_method = 1;
            nextForm.payment_number = '';
        }
        
        // Auto-set current date for operations that don't need date picker
        if (field === 'status') {
            // Ensure a default date exists; user can change it via date picker
            if (!nextForm.date_belonged) {
                nextForm.date_belonged = getCurrentDateString();
            }
        }
        
        setOpForm(nextForm);
    }, [opForm]);

    const submit = useCallback(async (options = {}) => {
        if (!selectedEnrollment) return;
        const acknowledgeOldFee = Boolean(options.acknowledgeOldFee);

        // Renewal fee guard (client side, backend enforces the same rule): a
        // registration on an enrollment whose frozen fee lags the live course fee
        // needs an explicit choice from the user before anything is posted.
        const statusNum = Number(opForm.status);
        if ([1, 10].includes(statusNum) && !acknowledgeOldFee) {
            const frozenFee = Number(
                selectedEnrollment.fee ??
                ((Number(selectedEnrollment.final_fee) || 0) + (Number(selectedEnrollment.discount_amount) || 0))
            );
            const currentFee = Number(selectedEnrollment?.course?.current_fee);
            if (Number.isFinite(currentFee) && frozenFee !== currentFee) {
                setStaleFee({ enrollmentFee: frozenFee, currentFee });
                return;
            }
        }

        setSubmitting(true);
        setError('');

        try {
            const payload = {
                student_and_course_id: selectedEnrollment.id,
                status_code: Number(opForm.status),
                comment: opForm.comment || '',
                date_belonged: opForm.date_belonged || undefined,
            };

            // Only include payment method for financial operations (registration / refund)
            if ([1, 9, 10].includes(Number(opForm.status))) {
                payload.payment_method = Number(opForm.payment_method) || 1;
                payload.payment_number = opForm.payment_number ? Number(opForm.payment_number) : undefined;
            }

            if ([7, 8, 9, 10].includes(Number(opForm.status))) {
                payload.session_count = opForm.session_count ? Number(opForm.session_count) : undefined;
            }

            if (acknowledgeOldFee) {
                payload.acknowledge_old_fee = true;
            }

            await api.post(API.studentOperations, payload);
            
            // Show success message
            setMessage({ type: 'success', text: 'عملیات با موفقیت ثبت شد' });
            
            // Clear form except date
            setOpForm(prev => ({
                status: '',
                date_belonged: prev.date_belonged, // Keep the date
                comment: '',
                payment_method: 1,
                payment_number: '',
                session_count: prev.session_count
            }));
            
            // Clear any previous errors
            setError('');
            
            // Call success callback
            if (onSuccess) {
                onSuccess();
            }
            
            // Refresh data
            if (onDataRefresh) {
                await onDataRefresh();
            }
        } catch (e) {
            // Backend renewal fee guard (authoritative fallback for the client check).
            // DRF list-wraps scalar values in validation errors.
            const data = e?.response?.data;
            const unwrap = (v) => (Array.isArray(v) ? v[0] : v);
            if (unwrap(data?.error_code) === 'stale_enrollment_fee') {
                setStaleFee({
                    enrollmentFee: Number(unwrap(data.enrollment_fee)) || 0,
                    currentFee: Number(unwrap(data.current_course_fee)) || 0,
                });
                return;
            }
            const msg = e?.response?.data?.detail || e?.response?.data?.status || e?.response?.data?.non_field_errors?.[0] || 'خطا در ثبت عملیات';
            setError(typeof msg === 'string' ? msg : 'خطا در ثبت عملیات');
        } finally {
            setSubmitting(false);
        }
    }, [selectedEnrollment, opForm, onSuccess, onDataRefresh]);

    const clearMessage = useCallback(() => {
        setMessage({ type: '', text: '' });
    }, []);

    const clearStaleFee = useCallback(() => {
        setStaleFee(null);
    }, []);

    const submitWithOldFee = useCallback(() => {
        setStaleFee(null);
        return submit({ acknowledgeOldFee: true });
    }, [submit]);

    return {
        opForm,
        submitting,
        error,
        message,
        staleFee,
        clearStaleFee,
        submitWithOldFee,
        handleChange,
        submit,
        clearMessage
    };
}

